import { useCallback } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import useTaskCreate from "shared/Stack/useTaskCreate";
import Tooltip from "ds/components/Tooltip";
import { AccessResult } from "utils/access";

import { useRunShortcut } from "../../hooks/useRunShortcut";

// STACK_TASK_CREATE passes no run reference and no runtime config, so this runs
// against the stack's current configuration rather than reusing the task's own.
const CURRENT_CONFIG_MESSAGE = "Current config will be used for running this task again.";

type PerformTaskAgainButtonProps = {
  command: string | null;
  stackId: string;
  canPerformAgain: AccessResult;
  runQueryToRefetch: string;
};

const PerformTaskAgainButton = ({
  command,
  stackId,
  canPerformAgain,
  runQueryToRefetch,
}: PerformTaskAgainButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const [createTask, { loading, data }] = useTaskCreate({
    refetchQueries: [runQueryToRefetch],
  });

  const isLoading = loading || !!data?.taskCreate?.id;

  const performTaskAgain = useCallback(() => {
    createTask({ stack: stackId, command: command || "", skipInitialization: false }, (data) => {
      if (data?.taskCreate?.id) {
        const taskId = data.taskCreate.id;
        reportSuccess({ message: `Task ${taskId} successfully triggered` });
        navigate(`/stack/${stackId}/run/${taskId}`);
      }
    });
  }, [createTask, stackId, command, reportSuccess, navigate]);

  useRunShortcut({
    label: "Perform again",
    code: "mod+alt+p",
    callback: performTaskAgain,
    loading: isLoading,
    disabled: !canPerformAgain.result,
  });

  const tooltipMessage = canPerformAgain.message || CURRENT_CONFIG_MESSAGE;

  return (
    <Tooltip
      on={(props) => (
        <Button
          {...props}
          variant="primary"
          loading={isLoading}
          disabled={isLoading || !canPerformAgain.result}
          onPress={performTaskAgain}
        >
          Perform again
        </Button>
      )}
      active
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default PerformTaskAgainButton;
